import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Sparkle, RocketLaunch, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
export default function LandingPage() {
  const [serviceFocus, setServiceFocus] = useState("");
  const [headlineHint, setHeadlineHint] = useState("");
  const [cta, setCta] = useState("Get a free quote");
  const [current, setCurrent] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/landing").then(r => setList(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!serviceFocus) return toast.error("Service focus required");
    setLoading(true);
    try {
      const r = await api.post("/landing", { service_focus: serviceFocus, headline_hint: headlineHint, cta });
      setCurrent(r.data); toast.success("Landing page generated"); load();
    } catch { toast.error("Failed"); } finally { setLoading(false); }
  };

  return (
    <div data-testid="landing-page">
      <PageHeader eyebrow="Lead Capture" title="Landing pages that convert visitors into jobs." description="Conversion-optimised copy in seconds. Tweak, copy, ship." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="sharp-card p-6">
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Service focus</Label>
              <Input data-testid="lp-focus" value={serviceFocus} onChange={(e)=>setServiceFocus(e.target.value)} placeholder="e.g. kitchen handyman services" className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Headline hint</Label>
              <Input data-testid="lp-hint" value={headlineHint} onChange={(e)=>setHeadlineHint(e.target.value)} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Primary CTA</Label>
              <Input data-testid="lp-cta" value={cta} onChange={(e)=>setCta(e.target.value)} className="rounded-sm mt-1" />
            </div>
            <Button data-testid="btn-gen-landing" onClick={generate} disabled={loading} className="w-full bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-11">
              <Sparkle className="mr-2" size={18} />{loading ? "Generating..." : "Generate landing copy"}
            </Button>
          </div>

          {list.length > 0 && (
            <div className="mt-8">
              <div className="eyebrow mb-3">Previously generated</div>
              <div className="space-y-2">
                {list.map((l) => (
                  <div key={l.id} className="flex items-center gap-2">
                    <button onClick={()=>setCurrent(l)} className="flex-1 text-left p-3 border border-slate-200 rounded-sm hover:border-navy transition-colors bg-white">
                      <div className="text-sm font-semibold text-navy">{l.headline}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{l.service_focus}</div>
                    </button>
                    <button data-testid={`del-landing-${l.id}`} onClick={async ()=>{ if(!window.confirm("Delete this landing page?")) return; await api.delete(`/landing/${l.id}`); toast.success("Deleted"); load(); if(current?.id===l.id) setCurrent(null);}} className="p-2 text-slate-400 hover:text-red-500 transition-colors" title="Delete"><Trash size={14}/></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <div className="bg-navy-deep rounded-sm overflow-hidden border border-slate-200">
          <div className="px-4 py-2 bg-slate-800 flex items-center gap-2 border-b border-slate-700">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-navy" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
            <div className="text-xs text-slate-400 ml-2">preview · truenorthmaintenance.com.au{current?.slug ? `/p/${current.slug}` : "/p/[slug]"}</div>
          </div>
          <div className="bg-paper p-6 md:p-10">
            {!current ? (
              <div className="text-center text-slate-400 py-20">
                <RocketLaunch size={36} className="mx-auto" />
                <p className="mt-3 text-sm">Your landing page preview will appear here.</p>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-3 mb-5 pb-5 border-b border-slate-200">
                  <img src="https://customer-assets.emergentagent.com/job_maintenance-master-8/artifacts/lvqicc5n_White%20Logo.png" alt="" className="h-9 invert opacity-90"/>
                  <div className="leading-tight">
                    <div className="font-display tracking-wider text-navy text-sm">TRUE NORTH</div>
                    <div className="font-display tracking-[0.22em] text-slate-500 text-[10px]">MAINTENANCE</div>
                  </div>
                </div>
                <div className="eyebrow eyebrow-dark mb-3">Trusted local property maintenance · Albury / Wodonga</div>
                <h1 className="font-display display-lg tracking-wide text-navy leading-[0.92]">{current.headline}</h1>
                <p className="mt-4 text-slate-700 text-base leading-relaxed font-light">{current.subheadline}</p>
                <Button className="mt-5 pill-navy">{current.cta}</Button>
                <div className="mt-8 grid grid-cols-2 gap-3">
                  {current.benefits.map((b) => (
                    <div key={b} className="flex items-start gap-2 text-sm text-slate-800"><CheckCircle size={16} weight="fill" className="text-navy mt-0.5 shrink-0" /><span>{b}</span></div>
                  ))}
                </div>
                <div className="mt-8 p-4 bg-cream border-l-2 border-navy text-sm italic text-slate-700 font-light">"{current.testimonial_quote}"</div>
                <div className="mt-6 text-xs text-slate-500 border-t border-slate-100 pt-3 space-y-1">
                  <div>📞 0478 350 778 · ✉ blake@truenorthmaintenance.com.au</div>
                </div>
                {current.slug && (
                  <div className="mt-6 p-3 bg-navy-deep text-white rounded-sm flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-medium">Public URL · live</div>
                      <a href={`/p/${current.slug}`} target="_blank" rel="noreferrer" className="text-sm font-mono truncate block hover:text-white/80">/p/{current.slug}</a>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button data-testid="btn-copy-url" size="sm" variant="outline" className="rounded-sm bg-transparent border-slate-700 text-white hover:bg-slate-800" onClick={()=>{navigator.clipboard.writeText(window.location.origin + `/p/${current.slug}`); toast.success("URL copied");}}>Copy URL</Button>
                      <a href={`/p/${current.slug}`} target="_blank" rel="noreferrer"><Button size="sm" className="rounded-sm bg-white hover:bg-white/90 text-navy-deep">Open</Button></a>
                    </div>
                  </div>
                )}
                <div className="mt-6 text-xs text-slate-400 border-t border-slate-100 pt-3">SEO Title: {current.seo_title}<br/>Meta: {current.seo_description}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
